import React from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Link,
  Tooltip,
} from '@mui/material';
import type { TypographyProps } from '@mui/material';
import { useUser } from '@clerk/clerk-react';
import { useTranslations } from '@/i18n/use-translations';
import { useNotifications } from '@/hooks/use-notifications';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import DeleteIcon from '@mui/icons-material/Delete';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import ConfirmationModal from '@/components/common/confirmation-modal';
import OtpCode from '@/components/invitation-flow/otp-code';

const emailSchema = z.object({
  email: z.string().email('Invalid email address'),
});

type EmailForm = z.infer<typeof emailSchema>;

type MySettingsTabProps = {
  tabTitle?: TypographyProps;
};

const EmailManagementTab: React.FC<MySettingsTabProps> = ({ tabTitle }) => {
  const t = useTranslations();
  const { user } = useUser();
  const notifications = useNotifications();
  const [isLoading, setIsLoading] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [pendingEmail, setPendingEmail] = useState<string | null>(null);
  const [showAddEmail, setShowAddEmail] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [makePrimaryModalOpen, setMakePrimaryModalOpen] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState<string | null>(null);
  const [otpError, setOtpError] = useState(false);

  const form = useForm<EmailForm>({
    resolver: zodResolver(emailSchema),
    mode: 'onBlur',
  });

  const handleAddEmail = async (data: EmailForm) => {
    setIsLoading(true);
    try {
      if (!user) throw new Error('User not found');

      // Create email address
      const emailAddress = await user.createEmailAddress({
        email: data.email,
      });

      // Prepare verification
      await emailAddress.prepareVerification({
        strategy: 'email_code',
      });

      setPendingEmail(data.email);
      setIsVerifying(true);
      notifications.notifySuccess(t('user.notification.verificationCodeSent'));
      setShowAddEmail(false);
    } catch (error) {
      console.error('Failed to add email:', error);
      notifications.notifyError(t('user.notification.addEmailError'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyCode = async (code: string) => {
    if (!pendingEmail) return;

    setIsLoading(true);
    try {
      if (!user) throw new Error('User not found');

      const emailAddress = user.emailAddresses.find((e) => e.emailAddress === pendingEmail);
      if (!emailAddress) throw new Error('Email address not found');

      await emailAddress.attemptVerification({ code });

      notifications.notifySuccess(t('user.notification.emailVerified'));
      setPendingEmail(null);
      setIsVerifying(false);
      setOtpError(false);
      form.reset();
    } catch (error) {
      console.error('Verification failed:', error);
      notifications.notifyError(t('user.notification.verificationFailed'));
      setOtpError(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoveEmail = async (email: string) => {
    setIsLoading(true);
    try {
      if (!user) throw new Error('User not found');

      const emailAddress = user.emailAddresses.find((e) => e.emailAddress === email);
      if (!emailAddress) throw new Error('Email address not found');

      await emailAddress.destroy();

      notifications.notifySuccess(t('user.notification.emailRemoved'));
    } catch (error) {
      console.error('Failed to remove email:', error);
      notifications.notifyError(t('user.notification.removeEmailError'));
    } finally {
      setIsLoading(false);
      setDeleteModalOpen(false);
      setSelectedEmail(null);
    }
  };

  const handleSetPrimaryEmail = async (email: string) => {
    setIsLoading(true);
    try {
      if (!user) throw new Error('User not found');

      const emailAddress = user.emailAddresses.find((e) => e.emailAddress === email);
      if (!emailAddress) throw new Error('Email address not found');

      await user.update({
        primaryEmailAddressId: emailAddress.id,
      });

      notifications.notifySuccess(t('user.notification.updateSuccess'));
    } catch (error) {
      console.error('Failed to set primary email:', error);
      notifications.notifyError(t('user.notification.updateError'));
    } finally {
      setIsLoading(false);
      setMakePrimaryModalOpen(false);
      setSelectedEmail(null);
    }
  };

  // Using shared OtpCode component for verification input

  const handleStartVerification = async (email: string) => {
    setIsLoading(true);
    try {
      if (!user) throw new Error('User not found');

      const emailAddress = user.emailAddresses.find((e) => e.emailAddress === email);
      if (!emailAddress) throw new Error('Email address not found');

      await emailAddress.prepareVerification({
        strategy: 'email_code',
      });

      setPendingEmail(email);
      setIsVerifying(true);
      setOtpError(false);
      notifications.notifySuccess(t('user.notification.verificationCodeSent'));
    } catch (error) {
      console.error('Failed to start verification:', error);
      notifications.notifyError(t('user.notification.verificationStartError'));
    } finally {
      setIsLoading(false);
    }
  };

  // Sort emails to show primary first
  const sortedEmails = user?.emailAddresses.sort((a, b) => {
    if (a.id === user.primaryEmailAddressId) return -1;
    if (b.id === user.primaryEmailAddressId) return 1;
    return 0;
  });

  return (
    <Box sx={{ p: 2, pt: 0 }}>
      <Typography {...tabTitle}>{t('userSettings.email')}</Typography>
      {isVerifying ? (
        <Box>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {t('user.email.verificationCodeSentTo', { email: pendingEmail || '' })}
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <OtpCode
              autoFocus
              hasError={otpError}
              onChange={(code) => {
                if (otpError) setOtpError(false);
                if (code.length === 6) {
                  void handleVerifyCode(code);
                }
              }}
            />
          </Box>
          <Button
            variant="outlined"
            onClick={() => {
              setPendingEmail(null);
              setIsVerifying(false);
              setOtpError(false);
            }}
            disabled={isLoading}
          >
            {t('common.cancel')}
          </Button>
        </Box>
      ) : (
        <>
          <List sx={{ py: 0, mb: 3 }}>
            {sortedEmails?.map((email) => (
              <ListItem key={email.id} sx={{ py: 0 }}>
                <ListItemText
                  primary={email.emailAddress}
                  primaryTypographyProps={{ variant: 'body2' }}
                  secondary={
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 0 }}>
                      <Typography
                        variant="body2"
                        color={email.verification?.status === 'verified' ? 'info.main' : 'error.main'}
                        component="span"
                      >
                        {email.verification?.status === 'verified'
                          ? t('user.email.verified')
                          : t('user.email.unverified')}
                      </Typography>
                      {email.verification?.status !== 'verified' && (
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => handleStartVerification(email.emailAddress)}
                          disabled={isLoading}
                        >
                          {t('user.email.verify')}
                        </Button>
                      )}
                      {user?.primaryEmailAddressId === email.id && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <StarIcon fontSize="small" color="primary" />
                          <Typography variant="body2" color="primary" component="span">
                            {t('user.email.primary')}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  }
                  secondaryTypographyProps={{ component: 'div' }}
                />
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {user?.primaryEmailAddressId !== email.id && (
                    <Tooltip title={t('user.email.makePrimary')}>
                      <IconButton
                        edge="end"
                        onClick={() => {
                          setSelectedEmail(email.emailAddress);
                          setMakePrimaryModalOpen(true);
                        }}
                        disabled={isLoading || email.verification?.status !== 'verified'}
                        sx={{ mr: 1 }}
                      >
                        <StarBorderIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                  <IconButton
                    edge="end"
                    onClick={() => {
                      setSelectedEmail(email.emailAddress);
                      setDeleteModalOpen(true);
                    }}
                    disabled={isLoading || user?.primaryEmailAddressId === email.id}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              </ListItem>
            ))}
          </List>

          {!showAddEmail ? (
            <Link
              component="button"
              variant="body2"
              onClick={() => setShowAddEmail(true)}
              sx={{
                'mt': 1,
                'display': 'block',
                'pl': 2.5,
                'textDecoration': 'none',
                '&:hover': {
                  textDecoration: 'underline',
                },
              }}
            >
              + {t('user.email.addNew')}
            </Link>
          ) : (
            <form onSubmit={form.handleSubmit(handleAddEmail)}>
              <Box sx={{ display: 'flex', gap: 1, mt: 1, height: '40px', alignItems: 'center', pl: 2.5 }}>
                <TextField
                  fullWidth
                  type="email"
                  size="small"
                  variant="standard"
                  {...form.register('email')}
                  error={!!form.formState.errors.email}
                  helperText={form.formState.errors.email?.message}
                  FormHelperTextProps={{ sx: { position: 'absolute', top: '-20px' } }}
                />
                <Button
                  type="submit"
                  variant="contained"
                  size="small"
                  disabled={isLoading || !form.formState.isValid}
                  sx={{ minWidth: 'auto', px: 1 }}
                >
                  {t('common.add')}
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setShowAddEmail(false)}
                  sx={{ minWidth: 'auto', px: 1 }}
                >
                  {t('common.cancel')}
                </Button>
              </Box>
            </form>
          )}
        </>
      )}

      <ConfirmationModal
        open={deleteModalOpen}
        onCancel={() => {
          setDeleteModalOpen(false);
          setSelectedEmail(null);
        }}
        onConfirm={() => selectedEmail && handleRemoveEmail(selectedEmail)}
        title={t('user.email.deleteModal.title')}
        text={t('user.email.deleteModal.text', { email: selectedEmail || '' })}
      />

      <ConfirmationModal
        open={makePrimaryModalOpen}
        onCancel={() => {
          setMakePrimaryModalOpen(false);
          setSelectedEmail(null);
        }}
        onConfirm={() => selectedEmail && handleSetPrimaryEmail(selectedEmail)}
        title={t('user.email.makePrimaryModal.title')}
        text={t('user.email.makePrimaryModal.text', { email: selectedEmail || '' })}
      />
    </Box>
  );
};

export default EmailManagementTab;
